import { type CustomerAnalyticsMoodysReportDto } from '@zg-rentals/ts-customer-analytics-client';
import { MarketTrendsPageWrapper, MarketTrendsTextHeader } from './MarketTrendsComponents';
import { Image, View } from '@react-pdf/renderer';
import { styles } from './pdfStyles';

import { Bar, BarChart, CartesianGrid, Legend, XAxis, YAxis } from 'recharts';
import { Label } from 'recharts';
import { renderChartToPdf } from './pdfImageUtils';

interface PerformanceChartProps {
  report: CustomerAnalyticsMoodysReportDto;
}

export const DemographicsChart = ({ report }: PerformanceChartProps) => {
  if (!report.inventoryReport) return null;

  const regionLabel = `${report.regionName} Region `;
  const marketLabel = `${report.marketName} `;
  const nationLabel = 'United States ';

  const employmentData = report.demographicsReport
    .sort((a, b) => a.year - b.year)
    .map((item) => ({
      name: item.year,
      region: item.regionEmploymentChange,
      market: item.marketEmploymentChange,
      nation: item.nationalEmploymentChange,
    }));

  const popData = report.demographicsReport
    .sort((a, b) => a.year - b.year)
    .map((item) => ({
      name: item.year,
      region: item.regionPopulationChange,
      market: item.marketPopulationChange,
      nation: item.nationalPopulationChange,
    }));

  return (
    <>
      <BarChart
        width={1400}
        height={650}
        data={employmentData}
        margin={{
          top: 10,
          right: 5,
          left: 20,
          bottom: 30,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis tickFormatter={(value: number) => (value * 100).toFixed(2)}>
          <Label
            value="Total Employment (% Change)"
            offset={-8}
            angle={-90}
            position="insideLeft"
            style={{ textAnchor: 'middle', fontSize: '20px' }}
          />
        </YAxis>
        <Legend
          wrapperStyle={{ position: 'relative', fontSize: '20px', marginTop: '-15px', fontFamily: 'sans-serif' }}
        />
        <Bar
          barSize={20}
          isAnimationActive={false}
          dataKey="region"
          fill="#006AFF"
          name={regionLabel + 'Total Employment'}
        />
        <Bar
          barSize={20}
          isAnimationActive={false}
          dataKey="market"
          fill="#001851"
          name={marketLabel + 'Total Employment'}
        />
        <Bar
          barSize={20}
          isAnimationActive={false}
          dataKey="nation"
          fill="#F2A619"
          name={nationLabel + 'Total Employment'}
        />
      </BarChart>
      <br /> <br />
      <br /> <br />
      <BarChart
        width={1400}
        height={650}
        data={popData}
        margin={{
          top: 10,
          right: 5,
          left: 20,
          bottom: 30,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis tickFormatter={(value: number) => (value * 100).toFixed(2)}>
          <Label
            value="Population (% Change)"
            offset={-8}
            angle={-90}
            position="insideLeft"
            style={{ textAnchor: 'middle', fontSize: '20px' }}
          />
        </YAxis>
        <Legend
          wrapperStyle={{ position: 'relative', fontSize: '20px', marginTop: '-15px', fontFamily: 'sans-serif' }}
        />
        <Bar barSize={20} isAnimationActive={false} dataKey="region" fill="#006AFF" name={regionLabel + 'Population'} />
        <Bar barSize={20} isAnimationActive={false} dataKey="market" fill="#001851" name={marketLabel + 'Population'} />
        <Bar barSize={20} isAnimationActive={false} dataKey="nation" fill="#F2A619" name={nationLabel + 'Population'} />
      </BarChart>
    </>
  );
};

export const DemographicsPage = ({
  report,
  demographicsChart,
}: {
  report: CustomerAnalyticsMoodysReportDto;
  demographicsChart: string;
}) => {
  return (
    <MarketTrendsPageWrapper quarter={report.dataAsOfQuarter} year={report.dataAsOfYear}>
      <View style={styles.containerBase}>
        <MarketTrendsTextHeader headerText="Employment and Population Change" paddingBottom={20} />
        <View
          style={{
            flexDirection: 'row',
            width: '100%',
            justifyContent: 'center',
            marginBottom: 20,
          }}
        >
          <View>
            <Image src={demographicsChart} />
          </View>
        </View>
      </View>
    </MarketTrendsPageWrapper>
  );
};

export const renderDemographicsPage = async (report: CustomerAnalyticsMoodysReportDto): Promise<string> => {
  return await renderChartToPdf(<DemographicsChart report={report} />);
};
